import React, { useEffect, useState } from 'react'
import { DownOutlined } from '@ant-design/icons'
import { useTranslation } from 'next-i18next'

interface GradeDropdownProps {
  selectedGrade: { key: string; label: string }
  grades: Record<string, string>
  onGradeSelect: (grade: { key: string; label: string }) => void
  isLoading?: boolean
  currentGradeKey?: string
}

export default function GradeDropdown({
  selectedGrade,
  grades,
  onGradeSelect,
  isLoading,
  currentGradeKey,
}: GradeDropdownProps): React.ReactElement {
  const { t } = useTranslation('statisticsV2')
  const [isOpen, setIsOpen] = useState(false)
  /**
   * Whether the selection on screen is one of THIS dropdown's own grades.
   *
   * 🔴 The page can hold the selection elsewhere — the teacher panel's class-grade button, and the
   * All math button before it — and the two controls are one selection between them. So when the key
   * is not a grade in this list, the dropdown drops to its placeholder AND to the unselected border;
   * highlighting it would leave two controls both looking chosen. Previously this was a literal
   * `=== 'all-math'` test, which silently stopped dimming the border the moment another key appeared.
   *
   * ⚠ `|| selectedGrade.label`: the b2c page seeds `{ key: '0', label: 'All grades' }` from an effect
   * BEFORE `grades` lands — and keeps it if `/get-user-stats` errors — so keying on `grades` alone
   * showed a placeholder over a page that was genuinely on all-grades. A sentinel selection carries an
   * empty label by design, so this still falls through for it.
   */
  const isOwnGradeSelected = Boolean(grades[selectedGrade.key]) || Boolean(selectedGrade.label)
  const isCurrentGradeSelected = Boolean(currentGradeKey) && selectedGrade.key === currentGradeKey

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as HTMLElement
      if (!target.closest('.grade-dropdown')) {
        setIsOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  return (
    <div className="relative grade-dropdown">
      <button
        className={`flex justify-between bg-purple-darker/60 min-w-65 leading-tight w-full ${
          isLoading ? 'opacity-50 cursor-not-allowed' : ''
        } text-white p-3 md:px-4 md:py-3 h-13 rounded-4 font-black border-2 flex items-center gap-2 md:gap-4 hover:bg-purple-darker/80 transition-all shadow-lg group ${
          isOwnGradeSelected ? 'border-green-lime' : 'border-white/10'
        }`}
        disabled={isLoading}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="flex justify-center items-center gap-1 text-sm md:text-md font-bold">
          {isOwnGradeSelected ? (
            <>
              {selectedGrade.label}{' '}
              {isCurrentGradeSelected && (
                <span className="text-green-emerald text-xs lg:flex hidden">({t('current')})</span>
              )}
            </>
          ) : (
            t('selectGrade')
          )}
        </span>
        <DownOutlined className={`text-sm transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <div
        className={`absolute top-full left-0 mt-3 min-w-40 w-full bg-purple-darker border border-white/10 rounded-4 overflow-hidden shadow-2xl z-50 origin-top transition-all duration-200 ease-out ${
          isOpen
            ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
            : 'opacity-0 -translate-y-2 scale-95 pointer-events-none'
        }`}
      >
        <div className="flex flex-col justify-center items-center gap-1 px-2 py-2">
          {Object.entries(grades).map(([key, value]) => (
            <button
              className={`w-full text-left rounded transition-colors font-bold py-3 px-2 ${
                key === selectedGrade.key ? 'bg-green-lime text-black' : 'text-white hover:bg-white/10'
              }`}
              key={key}
              onClick={() => {
                onGradeSelect({ key, label: value })
                setIsOpen(false)
              }}
            >
              <span className="flex flex-col md:flex-row items-start md:items-center justify-center md:justify-start gap-1">
                {value}
                {Boolean(currentGradeKey) && key === currentGradeKey && (
                  <span className={`text-xs ${key === selectedGrade.key ? 'text-black/70' : 'text-green-emerald'}`}>
                    ({t('current')})
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
